/**
 * PublicProfilePage.js — read-only profile view at /u/:userId.
 *
 * The opposite of ProfilePage.js:
 *   - ProfilePage.js: /profile, owner-only, every field editable inline.
 *     Reads getUserProfile.php which is gated by require_uid().
 *   - PublicProfilePage.js (this file): /u/:userId, ANY viewer, every
 *     field read-only. Reads getPublicProfile.php which is anonymous
 *     and serves only the publishable subset of fields.
 *
 * Reuses .profile-card / .profile-card__* styles from ProfilePage.css
 * so both surfaces share the same visual language — the only thing
 * missing here is the edit affordances, the photo upload row, and the
 * email line (PII gated by getPublicProfile.php).
 *
 * Self-viewing nuance: if the signed-in viewer happens to be the
 * profile's owner (matched by user_id), we show a "View edit page"
 * banner that links to /profile. We do NOT render inline edit
 * controls here — keeping edit on one canonical surface avoids two
 * inconsistent write paths and keeps PR #48 narrow.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../authContext';
import { apiCall, ApiError } from '../api';
import { ORG_TYPE_OPTIONS } from '../utils/constants';
import './ProfilePage.css';
import './PublicProfilePage.css';

function labelForOrg(orgType) {
  if (!orgType) return null;
  return ORG_TYPE_OPTIONS.find((o) => o.value === orgType)?.label || null;
}

export default function PublicProfilePage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { profile: ownerProfile } = useAuth();
  const numericUserId = Number(userId);
  const isValidId = Number.isFinite(numericUserId) && numericUserId > 0;

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ posts: 0, locations: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isValidId) {
      setError('Invalid profile URL.');
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiCall('getPublicProfile.php', {
      method: 'GET',
      query: { user_id: numericUserId },
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        setProfile(data?.profile || null);
        setStats({
          posts: Number(data?.stats?.posts) || 0,
          locations: Number(data?.stats?.locations) || 0,
        });
        setRecent(Array.isArray(data?.recent) ? data.recent : []);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        if (err instanceof ApiError && err.status === 404) {
          setError('Profile not found.');
        } else {
          setError(
            (err && err.message) || 'Could not load profile.',
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [numericUserId, isValidId]);

  // Did the signed-in viewer just open their own public profile? If so
  // we offer a one-click hop to /profile (the editable surface). We
  // can't simply auto-redirect — a contributor might genuinely want
  // to see how their card renders to others.
  const isViewingOwnProfile =
    ownerProfile &&
    profile &&
    Number(ownerProfile.user_id) === Number(profile.user_id);

  if (!isValidId) {
    return (
      <div className="profile-page">
        <div className="public-profile__error">
          {error || 'Invalid profile URL.'}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-page__loading">Loading profile…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <button
          type="button"
          className="profile-page__back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <div className="public-profile__error">{error}</div>
      </div>
    );
  }

  if (!profile) return null;

  const orgLabel = labelForOrg(profile.org_type);
  const websiteHref = profile.website_url || null;
  const photo = profile.photo_url || null;
  const displayName = profile.display_name || 'Unknown';

  return (
    <div className="profile-page">
      <button
        type="button"
        className="profile-page__back"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      {isViewingOwnProfile && (
        <div className="public-profile__self-banner">
          You&rsquo;re viewing your public profile.{' '}
          <button
            type="button"
            className="public-profile__self-banner-link"
            onClick={() => navigate('/profile')}
          >
            Edit your profile →
          </button>
        </div>
      )}

      <div className="profile-card">
        <div className="profile-card__header">
          {photo ? (
            <img
              className="profile-card__avatar"
              src={photo}
              alt={displayName}
            />
          ) : (
            <span className="profile-card__avatar" aria-hidden="true">
              no&nbsp;photo
            </span>
          )}
          <div className="profile-card__identity">
            <h1 className="profile-card__name">{displayName}</h1>
            {/* PR #48: email intentionally NOT rendered here — the
                /u/:userId endpoint deliberately scrubs PII fields.
                Owners see their email on /profile. */}
            {orgLabel && (
              <span className="profile-card__org-chip">{orgLabel}</span>
            )}
          </div>
          {/* No photo-upload buttons in the read-only view. */}
        </div>

        <div className="profile-card__stats">
          <div className="profile-card__stat">
            <div className="profile-card__stat-value">{stats.posts}</div>
            <div className="profile-card__stat-label">Posts</div>
          </div>
          <div className="profile-card__stat">
            <div className="profile-card__stat-value">{stats.locations}</div>
            <div className="profile-card__stat-label">Locations added</div>
          </div>
        </div>

        <div className="profile-card__fields">
          <div className="public-profile__field">
            <div className="public-profile__field-label">Website</div>
            <div className="public-profile__field-value">
              {websiteHref ? (
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="public-profile__link"
                >
                  {websiteHref}
                </a>
              ) : (
                <span className="public-profile__empty">No website added</span>
              )}
            </div>
          </div>

          <div className="public-profile__field">
            <div className="public-profile__field-label">About</div>
            <div className="public-profile__field-value">
              {profile.bio ? (
                <p className="public-profile__bio">{profile.bio}</p>
              ) : (
                <span className="public-profile__empty">No bio yet</span>
              )}
            </div>
          </div>

          {/* Wireframe I5 (PR #46): recent contributions preview —
              up to 3 most recent non-private rows across markers,
              polygons, and polylines. Each link deep-jumps into the
              project map with the right shape selected via the
              ?marker= / ?polygon= / ?polyline= deep-link param. The
              full table view ("See all contributions") is deferred. */}
          <div className="public-profile__field">
            <div className="public-profile__field-label">Recent contributions</div>
            <div className="public-profile__field-value">
              {recent.length === 0 ? (
                <span className="public-profile__empty">
                  No public contributions yet
                </span>
              ) : (
                <ul className="public-profile__recent">
                  {recent.map((entry) => {
                    const decade = entry.year
                      ? `${Math.floor(entry.year / 10) * 10}s`
                      : null;
                    const kindLabel =
                      entry.kind === 'polygon'
                        ? 'Region'
                        : entry.kind === 'polyline'
                          ? 'Route'
                          : 'Story';
                    const projectName =
                      entry.project_name || entry.project_slug || 'project';
                    const href = entry.project_slug
                      ? `/${entry.project_slug}?${entry.kind}=${entry.id}` +
                        (decade ? `&year=${Math.floor(entry.year / 10) * 10}` : '')
                      : null;
                    const labelText = `${kindLabel} in ${projectName}${decade ? ` · ${decade}` : ''}`;
                    return (
                      <li
                        key={`${entry.kind}-${entry.id}`}
                        className="public-profile__recent-item"
                      >
                        {href ? (
                          <a
                            href={href}
                            className="public-profile__link"
                          >
                            {labelText}
                          </a>
                        ) : (
                          <span>{labelText}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
